import { JsonValue } from '../../Utility/JsonValue';
import { assert } from '../../Utility/assert';
import { NotCachedError, UnknownUrlError } from './errors';
import {
    CacheConfiguration,
    Cache,
    FulfillAction,
    CacheRetrieveFn,
    CacheStoreFn,
    UpdateFn,
    RejectAction,
} from './types';

export class Caching {
    #urls: Array<CacheConfiguration>;

    #store: CacheStoreFn;
    #retrieve: CacheRetrieveFn;

    /**
     * The framework used to store and retrieve cached data via intercepts.
     *
     * @param store - The function used cache data.
     * @param retrieve - The function used to retrieve data.
     */
    constructor(store: CacheStoreFn, retrieve: CacheRetrieveFn) {
        this.#urls = [];
        this.#store = store;
        this.#retrieve = retrieve;
    }

    /**
     * Configure `url` to be cached.
     *
     * @see {@link CacheConfigFn}
     */
    add(
        type: Cache,
        url: string,
        options?: {
            excludeParams?: Array<string>;
            update?: UpdateFn;
        }
    ): void {
        if (type === Cache.GetAndUpdate && options?.update === undefined) {
            throw new ReferenceError('Missing `update` function.');
        }

        this.#urls.push({
            type,
            url,
            options: {
                excludeParams: options?.excludeParams ?? [],
                update: options?.update ?? null,
            },
        });
    }

    /**
     * Cache data when intercepting successful requests.
     */
    async interceptFulfilled(
        givenUrl: string,
        givenParams: { [x: string]: JsonValue },
        data: JsonValue
    ): Promise<[JsonValue, FulfillAction]> {
        if (this.#cacheVia(Cache.GetWithFallback, givenUrl)) {
            await this.#getWithFallback(givenUrl, givenParams, data);
            return [data, FulfillAction.Cached];
        }

        if (this.#cacheVia(Cache.GetAndUpdate, givenUrl)) {
            const newData = await this.#getAndUpdate(givenUrl, givenParams, data);
            return [newData, FulfillAction.CachedAndModified];
        }

        return [data, FulfillAction.NotCached];
    }

    /**
     * Retrieve cached date when intercepting failed requests.
     */
    async interceptRejected(
        givenUrl: string,
        givenParams: { [x: string]: JsonValue }
    ): Promise<[JsonValue, RejectAction]> {
        let cacheId;
        try {
            cacheId = this.#generateCacheId(givenUrl, givenParams);
        } catch (err) {
            if (err instanceof UnknownUrlError) {
                return [null, RejectAction.ErrorUnknownUrl];
            }
            throw err;
        }

        let retrievedData;
        try {
            retrievedData = await this.#retrieve(cacheId);
        } catch (err) {
            if (err instanceof NotCachedError) {
                return [null, RejectAction.ErrorNoCachedData];
            }
            throw err;
        }

        return [retrievedData, RejectAction.Retrieved];
    }

    /**
     * Check if the given URL should be cached a certain way.
     */
    #cacheVia(givenType: Cache, givenUrl: string): boolean {
        return (
            this.#urls.find(({ type, url }) => {
                return type === givenType && url === givenUrl;
            }) !== undefined
        );
    }

    /**
     * Generate a `cacheId` from the given URL and Query Parameters.
     */
    #generateCacheId(givenUrl: string, givenParams: { [x: string]: JsonValue }): string {
        const found = this.#urls.find(({ url }) => {
            return url === givenUrl;
        });

        if (found === undefined) {
            throw new UnknownUrlError('URL not configured to be cached.');
        }

        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(givenParams)) {
            if (found.options.excludeParams.includes(key)) {
                continue;
            }

            let strValue: string;
            if (Array.isArray(value)) {
                strValue = value.join(',');
            } else if (typeof value === 'string') {
                strValue = value;
            } else {
                strValue = JSON.stringify(value);
            }

            params.append(key, strValue);
        }
        params.sort();

        return `v2/${givenUrl}?${params.toString()}`;
    }

    /**
     * Will cache whatever the endpoint returns.
     */
    async #getWithFallback(
        givenUrl: string,
        givenParams: { [x: string]: JsonValue },
        data: JsonValue
    ): Promise<void> {
        const cacheId = this.#generateCacheId(givenUrl, givenParams);
        await this.#store(cacheId, data);
    }

    /**
     * Allows you to take existing cache data and merge it with the latest response from the API.
     */
    async #getAndUpdate(
        givenUrl: string,
        givenParams: { [x: string]: JsonValue },
        data: JsonValue
    ): Promise<JsonValue> {
        const cacheId = this.#generateCacheId(givenUrl, givenParams);

        let oldData;
        try {
            oldData = await this.#retrieve(cacheId);
        } catch (err) {
            if (err instanceof NotCachedError) {
                oldData = null;
            } else {
                throw err;
            }
        }

        // get this URL's Cache Configuration
        const found = this.#urls.find(({ url }) => {
            return url === givenUrl;
        });
        assert(found !== undefined && found.options.update !== null);

        // update and store data
        const updatedData = await found.options.update(oldData, data);
        await this.#store(cacheId, updatedData);

        return updatedData;
    }
}
